import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Wire-format mirrors the tile routes in
// src/flowstack/server/routes/tiles.py (B2). Used by the Tile picker
// + ManifestForm in the submission flow.

export interface TileSummary {
  name: string;
  version: string;
  description: string;
  minimum_security_level: number;
  requires_gpu: boolean;
  /** Which tile source served this tile: "local" or "bundle:<name>"
   * (EPIC-002 C2.4 — external tile-package bundles). */
  source: string;
}

export interface TileListResponse {
  tiles: TileSummary[];
}

/** Full manifest payload — round-trips ``TileManifest.model_dump``
 * so the form generator sees every field the schema declares.
 * Typed as ``Record<string, unknown>`` because the GUI's
 * :class:`ManifestForm` switches on each parameter's ``type`` /
 * ``source`` / ``values`` at render time rather than at compile time. */
export interface TileManifestResponse {
  manifest: Record<string, unknown>;
}

export const TILES_QUERY_KEY = ["tiles", "list"] as const;

export function tileManifestQueryKey(name: string): readonly unknown[] {
  return ["tiles", "manifest", name] as const;
}

export function useTiles() {
  return useQuery({
    queryKey: TILES_QUERY_KEY,
    queryFn: async (): Promise<TileSummary[]> => {
      const response = await apiFetch<TileListResponse>("/api/tiles");
      return response.tiles;
    },
    // Tile manifests are filesystem-backed and rarely change; long
    // stale time matches the actual data cadence.
    staleTime: 5 * 60_000,
  });
}

export function useTileManifest(name: string) {
  return useQuery({
    queryKey: tileManifestQueryKey(name),
    queryFn: async (): Promise<TileManifestResponse> =>
      apiFetch<TileManifestResponse>(`/api/tiles/${encodeURIComponent(name)}/manifest`),
    staleTime: 5 * 60_000,
    enabled: Boolean(name),
  });
}
